import { Trans, t } from "@lingui/macro";
import { useEffect, useRef, useState } from "react";

import { useAuth } from "context/AuthContext/AuthProvider";
import { useGlobalContext } from "context/GlobalContext/GlobalContextProvider";
import { upsertTrader } from "lib/apis/referral";
import { helperToast } from "lib/helperToast";
import { MAX_REFERRAL_CODE_LENGTH } from "lib/legacy";
import { getRootUrl } from "lib/url";
import { useDebounce } from "lib/useDebounce";
import useWallet from "lib/wallets/useWallet";

import Button from "components/Button/Button";

import { getCodeError, getReferralCodeTakenStatus } from "./referralsHelper";

function AddAffiliateCode({ handleCreateReferralCode, active }) {
  const { setConnectWalletModalOpen } = useGlobalContext();
  return (
    <div className="referral-card section-center mt-medium">
      <h2 className="title">
        <Trans>Generate Referral Code</Trans>
      </h2>
      <p className="sub-title">
        <Trans>
          Looks like you don't have a referral code to share. <br /> Create one now and start earning rebates!
        </Trans>
      </p>
      <div className="card-action">
        {active ? (
          <AffiliateCodeForm handleCreateReferralCode={handleCreateReferralCode} />
        ) : (
          <Button variant="primary" className="w-full !py-8" onClick={() => setConnectWalletModalOpen(true)}>
            <Trans>Connect Wallet</Trans>
          </Button>
        )}
      </div>
    </div>
  );
}

export function AffiliateCodeForm({ handleCreateReferralCode, callAfterSuccess }) {
  const [referralCode, setReferralCode] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef("");
  const [referralCodeCheckStatus, setReferralCodeCheckStatus] = useState("ok");
  const debouncedReferralCode = useDebounce(referralCode, 300);
  const { account, chainId } = useWallet();
  const { isAuthenticated, signIn } = useAuth();
  useEffect(() => {
    inputRef.current.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const checkCodeTakenStatus = async () => {
      if (error) {
        setReferralCodeCheckStatus("ok");
        return;
      }
      const { status: takenStatus } = await getReferralCodeTakenStatus(account, debouncedReferralCode, chainId);
      // ignore the result if the referral code to check has changed
      if (cancelled) {
        return;
      }
      if (takenStatus === "none") {
        setReferralCodeCheckStatus("ok");
      } else {
        setReferralCodeCheckStatus("taken");
      }
    };
    setReferralCodeCheckStatus("checking");
    checkCodeTakenStatus();
    return () => {
      cancelled = true;
    };
  }, [account, debouncedReferralCode, error, chainId]);

  function getButtonError() {
    if (!debouncedReferralCode) {
      return t`Enter a code`;
    }
    if (referralCodeCheckStatus === "taken") {
      return t`Code already taken`;
    }
    if (referralCodeCheckStatus === "checking") {
      return t`Checking code...`;
    }

    return false;
  }

  const buttonError = getButtonError();

  function getPrimaryText() {
    if (buttonError) {
      return buttonError;
    }

    if (isProcessing) {
      return t`Creating...`;
    }

    return t`Create`;
  }
  function isPrimaryEnabled() {
    if (buttonError) {
      return false;
    }
    if (error || isProcessing) {
      return false;
    }
    return true;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setIsProcessing(true);
    const trimmedCode = referralCode.trim();
    const { status: takenStatus } = await getReferralCodeTakenStatus(account, trimmedCode, chainId);
    if (["all", "current", "other"].includes(takenStatus)) {
      setIsProcessing(false);
    }

    if (takenStatus === "none" || takenStatus === "other") {
      try {
        const tx = await handleCreateReferralCode(trimmedCode);
        const receipt = await tx.wait();
        if (receipt.status === 1) {
          if (account) {
            try {
              if (!isAuthenticated) {
                const session = await signIn();
                if (!session) {
                  helperToast.error(t`Referral code created on-chain, but sign-in is required to sync.`);
                  return;
                }
              }
              await upsertTrader({
                address: account,
                referralCode: trimmedCode,
              });
            } catch (syncError) {
              helperToast.error(t`Referral code created on-chain, but backend sync failed.`);
              return;
            }
          }
          helperToast.success(t`Referral code created!`);
          setReferralCode("");
          if (callAfterSuccess) {
            callAfterSuccess();
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      } finally {
        setIsProcessing(false);
      }
    }
  }

  return (
    <div className="mt-[33px] flex w-full max-w-[368px] flex-col">
      <form onSubmit={handleSubmit} className="flex w-full flex-col">
        <p className="text-12 font-medium leading-[1.5] text-gray-lighter">
          <Trans>What would you like to use as ur code? eg. 0xeth</Trans>
        </p>
        {/* <input
          type="text"
          ref={inputRef}
          value={referralCode}
          disabled={isProcessing}
          // className={cx("text-input", { "mb-15": !error })}
          placeholder={t`Enter a code`}
          onChange={({ target }) => {
            const { value } = target;
            setReferralCode(value);
            setError(getCodeError(value));
          }}
        /> */}
        <div className="mt-[10px] flex h-[40px] w-full items-center justify-center gap-4 rounded-[28px] border border-solid border-white-alpha-4 bg-white-alpha-2 p-[8px] sm:h-[45px] sm:py-[11px] sm:pl-[22px] sm:pr-[11px]">
          <p className="hidden text-14 font-medium text-white-alpha-16 sm:block">{getRootUrl()}/referral=?</p>
          <input
            type="text"
            ref={inputRef}
            value={referralCode}
            disabled={isProcessing}
            maxLength={MAX_REFERRAL_CODE_LENGTH}
            // className={cx("text-input", { "mb-15": !error })}
            className="w-max min-w-0 flex-1 text-14 font-medium text-white"
            // placeholder={t`Enter a code`}
            onChange={({ target }) => {
              const { value } = target;
              setReferralCode(value);
              setError(getCodeError(value));
            }}
          />
        </div>
        <p className="mb-0 ml-auto mt-2 font-medium leading-[1.5] text-gray-lightest">
          {referralCode?.length || 0}/{MAX_REFERRAL_CODE_LENGTH}
        </p>
        {error && <p className="AffiliateCode-error">{error}</p>}
        <Button
          variant="primary"
          className="mt-0 w-full max-w-[180px] !py-10"
          type="submit"
          disabled={!isPrimaryEnabled()}
        >
          {getPrimaryText()}
        </Button>
      </form>
      <p className="mt-[41px] max-w-[272px] text-12 font-medium leading-[1.5] tracking-[0] text-gray-lighter">
        <Trans>Please note that your referral code can only be changes once</Trans>
      </p>
    </div>
  );
}

export default AddAffiliateCode;
